/**
 * BroadcastComposer.tsx — Admin broadcast 3-step wizard (Plan 08-05, D-08-10).
 *
 * 3-step flow (Maloum-style):
 *   Step 1 — Targeting:
 *     - Include-lists picker (ListPickerModal — "Send to")
 *     - Exclude-lists picker (ListPickerModal — "Exclude")
 *     - Estimated recipients line (union(include counts) − union(exclude counts); note "exact after snapshot")
 *     - Next →
 *
 *   Step 2 — Compose:
 *     - EN Textarea
 *     - messageKind toggle (Text / Free media / PPV)
 *     - When not 'text': compact vault media picker + whole-EUR price input (PPV only)
 *     - Per-fan-language note ("English-mode fans receive EN; others receive the DE translation")
 *     - "PPV/media broadcast is dev-only — not yet live in production (CSAM screener required)"
 *       Banner whenever messageKind !== 'text'
 *     - "Translate & preview DE" → POSTs campaign (create); shows read-only DE preview
 *     - Next →
 *
 *   Step 3 — Sign-off:
 *     - Publish-date datetime-local input (default Now → scheduledAt null; future → scheduledAt ISO)
 *     - Recipient count + paced ETA
 *     - ≥3-concurrent-cap warning badge
 *     - Sign-off checkbox + description
 *     - "Sign off & schedule" / "Sign off & start broadcast" button
 *     - "Back to compose" button
 *
 * Security:
 *   - Admin-only surface; sign-off is the only path to an active/scheduled campaign.
 *   - Translation fail-closed: sign-off disabled if messageDe is null.
 *   - PPV price ×100 is the single conversion boundary at the create-body boundary (T-08-22).
 *   - PPV/media disarmed banner is always shown when messageKind !== 'text' (T-08-23).
 *   - No live sends fire (SEND_LIVE_ARMED default OFF).
 */

import * as React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  broadcastsApi,
  adminVaultApi,
  type CampaignDto,
  type CreateCampaignBody,
  type AdminVaultFolderDto,
  type AdminVaultMediaItemDto,
} from '../../lib/api-client.js';
import {
  Button,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Textarea,
  Dialog,
} from '@crm/ui';
import { ListPickerModal } from './ListPickerModal.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format eta seconds into "~Xh Ym" */
function formatEta(etaSeconds: number | null | undefined): string {
  if (etaSeconds == null || etaSeconds <= 0) return 'N/A';
  const h = Math.floor(etaSeconds / 3600);
  const m = Math.floor((etaSeconds % 3600) / 60);
  const s = etaSeconds % 60;
  if (h > 0) return `~${h}h ${m}m`;
  if (m > 0) return `~${m}m`;
  return `~${s}s`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface BroadcastComposerProps {
  accountId: string;
  /** Called when campaign is created AND signed off */
  onCreated?: (campaign: CampaignDto) => void;
  /** Number of currently active campaigns (for the 3-cap warning) */
  activeCampaignCount?: number;
}

// ---------------------------------------------------------------------------
// Compact vault picker (inline, no separate file needed — admin-only path)
// ---------------------------------------------------------------------------

interface VaultPickerProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
  onAttach: (media: { uploadId: string; type: string; width: number; height: number }) => void;
}

function AdminVaultPicker({ open, onClose, accountId, onAttach }: VaultPickerProps) {
  const [selectedFolder, setSelectedFolder] = React.useState<AdminVaultFolderDto | null>(null);
  const [selectedMedia, setSelectedMedia] = React.useState<AdminVaultMediaItemDto | null>(null);

  React.useEffect(() => {
    if (!open) {
      setSelectedFolder(null);
      setSelectedMedia(null);
    }
  }, [open]);

  const foldersQuery = useQuery({
    queryKey: ['admin-vault-folders', accountId],
    queryFn: () => adminVaultApi.folders(accountId),
    enabled: open,
    staleTime: 60_000,
  });

  const mediaQuery = useQuery({
    queryKey: ['admin-vault-media', accountId, selectedFolder?.id],
    queryFn: () => adminVaultApi.media(accountId, selectedFolder!.id),
    enabled: open && selectedFolder !== null,
    staleTime: 30_000,
  });

  const handleAttach = () => {
    if (!selectedMedia) return;
    onAttach({
      uploadId: selectedMedia.uploadId,
      type: selectedMedia.type,
      width: selectedMedia.width,
      height: selectedMedia.height,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} title="Select media from vault" className="max-w-xl">
      <div className="flex flex-col gap-3" style={{ minHeight: '360px' }}>
        {/* Breadcrumb */}
        <div className="flex items-center gap-1 text-xs text-text-muted border-b border-border pb-2">
          <button
            className="hover:text-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 rounded"
            onClick={() => { setSelectedFolder(null); setSelectedMedia(null); }}
          >
            All folders
          </button>
          {selectedFolder && (
            <>
              <span>/</span>
              <span className="text-text truncate max-w-[200px]">{selectedFolder.name}</span>
            </>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto max-h-64">
          {!selectedFolder && (
            <>
              {foldersQuery.isLoading && <p className="text-sm text-text-muted p-3 text-center">Loading…</p>}
              {foldersQuery.isError && <p className="text-sm text-danger p-3 text-center">Could not load vault.</p>}
              {foldersQuery.data && (
                <div className="grid grid-cols-2 gap-2">
                  {foldersQuery.data.folders.map((folder) => (
                    <button
                      key={folder.id}
                      className="text-left border border-border rounded-lg p-2 hover:border-accent transition-colors bg-bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
                      onClick={() => setSelectedFolder(folder)}
                    >
                      <p className="text-sm font-medium text-text truncate">{folder.name}</p>
                      <p className="text-xs text-text-muted mt-0.5">
                        {folder.pictureCount}p · {folder.videoCount}v
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {selectedFolder && (
            <>
              {mediaQuery.isLoading && <p className="text-sm text-text-muted p-3 text-center">Loading…</p>}
              {mediaQuery.isError && <p className="text-sm text-danger p-3 text-center">Could not load media.</p>}
              {mediaQuery.data && (
                <div className="grid grid-cols-3 gap-2">
                  {mediaQuery.data.items.map((item) => {
                    const isSel = selectedMedia?.uploadId === item.uploadId;
                    return (
                      <button
                        key={item.uploadId}
                        className={[
                          'relative rounded-lg overflow-hidden border-2 transition-all aspect-square',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-bg focus-visible:ring-accent/60',
                          isSel ? 'border-accent ring-2 ring-accent' : 'border-border hover:border-accent/50',
                        ].join(' ')}
                        onClick={() => setSelectedMedia(isSel ? null : item)}
                        aria-pressed={isSel}
                        aria-label={`Select ${item.type} media`}
                      >
                        <div className="w-full h-20 bg-bg-card">
                          {(item.thumbnail?.url ?? item.url) && (
                            <img
                              src={item.thumbnail?.url ?? item.url}
                              alt=""
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          )}
                        </div>
                        {item.type === 'video' && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="bg-scrim rounded-full w-6 h-6 flex items-center justify-center">
                              <svg className="w-3 h-3 text-accent-fg" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M6.3 2.84A1 1 0 005 3.75v12.5a1 1 0 001.3.96l10-4.17a1 1 0 000-1.84l-10-4.17z" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 pt-1 border-t border-border">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          <Button size="sm" disabled={selectedMedia === null} onClick={handleAttach}>
            Attach media
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// BroadcastComposer
// ---------------------------------------------------------------------------

export function BroadcastComposer({
  accountId,
  onCreated,
  activeCampaignCount = 0,
}: BroadcastComposerProps) {
  // ── Step state ───────────────────────────────────────────────────────────
  const [step, setStep] = React.useState<'targeting' | 'compose' | 'signoff'>('targeting');

  // ── Targeting (Step 1) ───────────────────────────────────────────────────
  const [includeLists, setIncludeLists] = React.useState<string[]>([]);
  const [excludeLists, setExcludeLists] = React.useState<string[]>([]);

  // ── Compose (Step 2) ─────────────────────────────────────────────────────
  const [messageEn, setMessageEn] = React.useState('');
  const [messageKind, setMessageKind] = React.useState<'text' | 'free_media' | 'chat_product'>('text');
  const [vaultOpen, setVaultOpen] = React.useState(false);
  const [vaultMedia, setVaultMedia] = React.useState<{
    uploadId: string; type: string; width: number; height: number;
  } | null>(null);
  /** Whole-EUR price typed by the admin (×100 at create-body boundary — T-08-22) */
  const [ppvPriceEur, setPpvPriceEur] = React.useState<string>('');

  // ── Sign-off (Step 3) ────────────────────────────────────────────────────
  const [scheduledAt, setScheduledAt] = React.useState<string>(''); // datetime-local value
  const [signoffChecked, setSignoffChecked] = React.useState(false);

  // Created campaign (has DE preview, etaSeconds, totalRecipients)
  const [campaign, setCampaign] = React.useState<CampaignDto | null>(null);
  const [createError, setCreateError] = React.useState<string | null>(null);

  // ── Mutations ─────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: (body: CreateCampaignBody) => broadcastsApi.create(body),
    onSuccess: (result) => {
      setCampaign(result);
      setCreateError(null);
      setStep('signoff');
    },
    onError: (err: Error) => {
      setCreateError(err.message ?? 'Failed to create campaign');
    },
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => broadcastsApi.approve(id),
    onSuccess: (approved) => {
      setCampaign(approved);
      onCreated?.(approved);
      // Reset form
      setStep('targeting');
      setIncludeLists([]);
      setExcludeLists([]);
      setMessageEn('');
      setMessageKind('text');
      setVaultMedia(null);
      setPpvPriceEur('');
      setScheduledAt('');
      setSignoffChecked(false);
      setCampaign(null);
    },
    onError: (err: Error) => {
      setCreateError(err.message ?? 'Failed to approve campaign');
    },
  });

  // ── Handlers ─────────────────────────────────────────────────────────────

  function handleTranslateAndPreview() {
    if (!messageEn.trim()) return;
    setCreateError(null);
    // Single ×100 conversion at the boundary (T-08-22 — never double-convert)
    const ppvPriceMinor = messageKind === 'chat_product' && ppvPriceEur
      ? Math.round(parseFloat(ppvPriceEur) * 100)
      : undefined;

    createMutation.mutate({
      name: messageEn.slice(0, 30).trim() || 'Broadcast',
      messageEn: messageEn.trim(),
      accountId,
      includeLists: includeLists.length > 0 ? includeLists : undefined,
      excludeLists: excludeLists.length > 0 ? excludeLists : undefined,
      messageKind,
      vaultMediaId: vaultMedia?.uploadId,
      isPpv: messageKind === 'chat_product',
      ppvPriceMinor,
    });
  }

  function handleSignOff() {
    if (!campaign || !signoffChecked) return;
    approveMutation.mutate(campaign.id);
  }

  // ── Derived ───────────────────────────────────────────────────────────────

  const etaStr = formatEta(campaign?.etaSeconds);
  const atConcurrentCap = activeCampaignCount >= 3;
  const translating = createMutation.isPending;
  const signingOff = approveMutation.isPending;
  const isPpvOrMedia = messageKind !== 'text';

  // ── Step 1: Targeting ─────────────────────────────────────────────────────

  if (step === 'targeting') {
    return (
      <div className="flex flex-col gap-4 p-4">
        <h2 className="text-lg font-semibold text-text">Step 1 — Targeting</h2>

        {/* Include picker */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-text">Send to (include lists)</label>
          <ListPickerModal
            accountId={accountId}
            selected={includeLists}
            onConfirm={setIncludeLists}
            label="Send to"
          />
          {includeLists.length === 0 && (
            <p className="text-xs text-text-muted">Select at least one list to send to.</p>
          )}
        </div>

        {/* Exclude picker */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-text">Exclude (optional)</label>
          <ListPickerModal
            accountId={accountId}
            selected={excludeLists}
            onConfirm={setExcludeLists}
            label="Exclude"
          />
        </div>

        {/* Estimated recipients note */}
        {includeLists.length > 0 && (
          <p className="text-xs text-text-muted bg-bg-card border border-border rounded px-3 py-2">
            Exact recipient count is calculated after snapshot (at sign-off time).
            Fans in exclude lists, blocked fans, and muted fans will not receive the message.
          </p>
        )}

        {/* CTA */}
        <div className="flex gap-2">
          <Button
            variant="default"
            disabled={includeLists.length === 0}
            onClick={() => setStep('compose')}
          >
            Next: Compose message
          </Button>
        </div>
      </div>
    );
  }

  // ── Step 2: Compose ───────────────────────────────────────────────────────

  if (step === 'compose') {
    return (
      <div className="flex flex-col gap-4 p-4">
        <h2 className="text-lg font-semibold text-text">Step 2 — Compose</h2>

        {/* EN template */}
        <div className="flex flex-col gap-2">
          <label htmlFor="broadcast-en" className="text-sm font-medium text-text">
            Message (English)
          </label>
          <Textarea
            id="broadcast-en"
            rows={5}
            placeholder="Write your English message here…"
            value={messageEn}
            onChange={(e) => setMessageEn(e.target.value)}
            className="resize-none"
          />
        </div>

        {/* Message kind toggle */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-text">Message type</label>
          <div className="flex gap-2">
            {(['text', 'free_media', 'chat_product'] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                onClick={() => {
                  setMessageKind(kind);
                  if (kind === 'text') {
                    setVaultMedia(null);
                    setPpvPriceEur('');
                  }
                }}
                className={[
                  'px-3 py-1.5 text-sm rounded border transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
                  messageKind === kind
                    ? 'border-accent bg-accent/10 text-accent font-medium'
                    : 'border-border text-text-muted hover:text-text hover:border-accent/40',
                ].join(' ')}
              >
                {kind === 'text' ? 'Text only' : kind === 'free_media' ? 'Free media' : 'PPV'}
              </button>
            ))}
          </div>
        </div>

        {/* PPV / free media disarmed banner */}
        {isPpvOrMedia && (
          <Badge variant="warning" className="w-fit">
            PPV/media broadcast is dev-only — not yet live in production (CSAM screener required).
          </Badge>
        )}

        {/* Vault media picker + PPV price (when not plain text) */}
        {isPpvOrMedia && (
          <div className="flex flex-col gap-3 p-3 bg-bg-card border border-border rounded">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={() => setVaultOpen(true)}>
                {vaultMedia ? 'Change media' : 'Select media from vault'}
              </Button>
              {vaultMedia && (
                <span className="text-xs text-text-muted font-mono">
                  {vaultMedia.type} — {vaultMedia.uploadId.slice(0, 12)}…
                  <button
                    className="ml-2 text-danger hover:underline"
                    onClick={() => setVaultMedia(null)}
                  >
                    Remove
                  </button>
                </span>
              )}
            </div>

            {messageKind === 'chat_product' && (
              <div className="flex flex-col gap-1">
                <label htmlFor="ppv-price" className="text-xs font-medium text-text-muted uppercase tracking-wide">
                  PPV price (EUR, whole number)
                </label>
                <Input
                  id="ppv-price"
                  type="number"
                  min={1}
                  step={1}
                  placeholder="e.g. 5"
                  value={ppvPriceEur}
                  onChange={(e) => setPpvPriceEur(e.target.value)}
                  className="w-32"
                />
                <p className="text-xs text-text-muted">
                  Enter the whole-EUR amount (e.g. 5 = €5.00). Stored as minor units (×100) at submission.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Vault picker dialog */}
        <AdminVaultPicker
          open={vaultOpen}
          onClose={() => setVaultOpen(false)}
          accountId={accountId}
          onAttach={(media) => setVaultMedia(media)}
        />

        {/* Per-fan language note */}
        <p className="text-xs text-text-muted bg-bg-card border border-border rounded px-3 py-2">
          English-mode fans receive the EN text directly. All other fans receive the EN→DE translation.
          Translation failure hard-blocks that language branch (no raw-EN fallback for German fans).
        </p>

        {/* Error */}
        {createError && (
          <p className="text-sm text-danger">{createError}</p>
        )}

        {/* CTAs */}
        <div className="flex gap-2">
          <Button
            variant="default"
            disabled={!messageEn.trim() || translating || (isPpvOrMedia && !vaultMedia) || (messageKind === 'chat_product' && !ppvPriceEur)}
            onClick={handleTranslateAndPreview}
          >
            {translating ? 'Translating…' : 'Translate & preview DE'}
          </Button>
          <Button
            variant="outline"
            onClick={() => setStep('targeting')}
            disabled={translating}
          >
            Back to targeting
          </Button>
        </div>
      </div>
    );
  }

  // ── Step 3: Sign-off ──────────────────────────────────────────────────────

  // Determine if this is a scheduled future send
  const isScheduled = scheduledAt.trim().length > 0;

  return (
    <div className="flex flex-col gap-4 p-4">
      <h2 className="text-lg font-semibold text-text">Step 3 — Sign Off</h2>

      {/* Broadcast summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Broadcast Summary</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {/* EN template read-back */}
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-text-muted uppercase tracking-wide">English</span>
            <p className="text-sm text-text bg-bg-input rounded px-3 py-2">{campaign?.messageEn}</p>
          </div>

          {/* DE preview (read-only) */}
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-text-muted uppercase tracking-wide">German preview (OB-1)</span>
            {campaign?.messageDe ? (
              <p className="text-sm text-text bg-bg-input rounded px-3 py-2">{campaign.messageDe}</p>
            ) : (
              <p className="text-sm text-danger">Translation failed — cannot sign off.</p>
            )}
          </div>

          {/* Stats */}
          <div className="flex gap-6 text-sm text-text-muted flex-wrap">
            <span>
              Recipients:{' '}
              <span className="text-text font-mono">{campaign?.totalRecipients ?? 0}</span>
            </span>
            <span>
              ETA:{' '}
              <span className="text-info font-mono">{etaStr}</span>
            </span>
            {messageKind !== 'text' && (
              <Badge variant="warning" className="self-center">
                PPV/media — dev-only (CSAM screener required)
              </Badge>
            )}
          </div>

          {/* Concurrent cap warning */}
          {atConcurrentCap && (
            <Badge variant="warning" className="w-fit">
              3 campaigns already active. This one will queue.
            </Badge>
          )}
        </CardContent>
      </Card>

      {/* Publish date picker */}
      <div className="flex flex-col gap-2">
        <label htmlFor="broadcast-schedule" className="text-sm font-medium text-text">
          Publishing date
        </label>
        <Input
          id="broadcast-schedule"
          type="datetime-local"
          value={scheduledAt}
          onChange={(e) => setScheduledAt(e.target.value)}
          className="w-60"
        />
        <p className="text-xs text-text-muted">
          {isScheduled
            ? `Will auto-fire at ${scheduledAt} (no second confirm needed).`
            : 'Leave empty to start immediately after sign-off.'}
        </p>
      </div>

      {/* Sign-off checkbox */}
      <div className="flex items-start gap-3 p-3 bg-bg-card border border-border rounded">
        <input
          id="broadcast-signoff-checkbox"
          type="checkbox"
          checked={signoffChecked}
          onChange={(e) => setSignoffChecked(e.target.checked)}
          className="mt-0.5 accent-accent h-4 w-4"
          disabled={!campaign?.messageDe}
        />
        <label
          htmlFor="broadcast-signoff-checkbox"
          className="text-sm text-text cursor-pointer select-none"
        >
          I have reviewed the German translation and confirm this broadcast.
          {messageKind !== 'text' && (
            <span className="block text-xs text-warning mt-1">
              PPV/media sends are disarmed — no live charges will fire until production CSAM screening is enabled.
            </span>
          )}
        </label>
      </div>

      {/* Error */}
      {createError && (
        <p className="text-sm text-danger">{createError}</p>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          variant="success"
          disabled={!signoffChecked || !campaign?.messageDe || signingOff}
          onClick={handleSignOff}
        >
          {signingOff
            ? 'Scheduling…'
            : isScheduled
            ? 'Sign off & schedule'
            : 'Sign off & start broadcast'}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            setStep('compose');
            setSignoffChecked(false);
          }}
          disabled={signingOff}
        >
          Back to compose
        </Button>
      </div>
    </div>
  );
}
